/**
 * ADR-0185 §3 (seam per Addendum 3) — the board SHELL, domain-blind by
 * construction: the composed read, the always-visible situation line, the
 * Re-solve / What-if / promotion gestures, the what-changed strip (never a
 * silent repaint), and the inputs-changed light — everything that reads only
 * the model/template/proposal contract. What the plan LOOKS like is a view
 * registered by the model's template (components/board/viewRegistry); the
 * shell renders whichever view the template claims and knows nothing about
 * lanes, machines, or any other domain vocabulary.
 *
 * Viewers get the whole board read-only — every solve gesture is ABSENT (not
 * disabled) without content-edit authority on the model.
 *
 * Addendum 4: the board is the model home's DEFAULT TAB — this renders the
 * board frame alone (no page chrome); the model home owns the header and the
 * tab row. The retired `/app/optimize/:modelId/board` address redirects into
 * that tab.
 *
 * Phase B: the shell also owns the Refine loop's writes (pending moves,
 * replan, reset, promote) and watches a replan the way it watches any solve —
 * on land it reloads the board (the working copy moved server-side) and
 * hands the view the landed plan for its attribution strip.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { LoadingState, ErrorState, EmptyState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { formatTimestamp, formatTimestampOr } from "../lib/formatTime.js";
import type {
  BoardPayload,
  BoardProposalView,
  BoardSolve,
  Placement,
  ScenarioEntry,
  VersusPrevious,
} from "../components/board/boardTypes.js";
import { overlaysDiffer } from "../components/board/overlayDiff.js";
import { boardViewFor, nextUpViewFor } from "../components/board/viewRegistry.js";
import "../components/board/views/index.js";
import { WhatChangedStrip } from "../components/board/WhatChangedStrip.js";

/**
 * Addendum 5 — the Next-up TAB's frame: the composed read, then the
 * template's registered next-up face (a read-only face of the standing
 * plan). Domain-blind like the board frame; a template without a next-up
 * face gets the honest empty state.
 */
export function OptimizeNextUpFrame(props: { modelId: string; onOpenBoard: (focus: string) => void }) {
  const [board, setBoard] = useState<BoardPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBoard(null);
    setError(null);
    void (async () => {
      try {
        const data = await apiFetch<BoardPayload>(`/api/tenant/optimize/models/${props.modelId}/board`);
        setBoard(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [props.modelId]);

  if (error) return <ErrorState detail={error} />;
  if (!board) return <LoadingState />;

  const NextUp = nextUpViewFor(board.template.id);
  if (!NextUp) {
    return (
      <div className="card">
        <EmptyState
          title="No next-up view for this kind of model yet."
          detail="Atlas doesn't yet have a next-up face that can read this model's plans."
        />
      </div>
    );
  }
  return <NextUp payload={board} view={board.standing} onOpenBoard={props.onOpenBoard} />;
}

export function OptimizeBoardFrame(props: { modelId: string; initialFocus?: string | null }) {
  const toast = useToast();
  const confirm = useConfirm();
  const modelId = props.modelId;

  const [board, setBoard] = useState<BoardPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [inFlight, setInFlight] = useState<BoardSolve | null>(null);
  const [elapsedS, setElapsedS] = useState(0);
  const [strip, setStrip] = useState<VersusPrevious | null>(null);
  const [solveNote, setSolveNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // What-if frame state.
  const [scenario, setScenario] = useState(false);
  const [scenarioEntries, setScenarioEntries] = useState<ScenarioEntry[]>([]);
  const [scenarioView, setScenarioView] = useState<BoardProposalView | null>(null);

  // Phase B — the last replan this session landed (the attribution strip).
  const [landedRefine, setLandedRefine] = useState<BoardProposalView | null>(null);

  // ---- loading -------------------------------------------------------------

  const loadBoard = useCallback(
    async (opts?: { quiet?: boolean }) => {
      if (!modelId) return;
      if (!opts?.quiet) setLoading(true);
      try {
        const data = await apiFetch<BoardPayload>(`/api/tenant/optimize/models/${modelId}/board`);
        setBoard(data);
        setError(null);
        if (data.in_flight && (data.in_flight.status === "queued" || data.in_flight.status === "running")) {
          setInFlight(data.in_flight);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [modelId]
  );

  useEffect(() => {
    setBoard(null);
    setScenario(false);
    setScenarioEntries([]);
    setScenarioView(null);
    setStrip(null);
    setSolveNote(null);
    setLandedRefine(null);
    void loadBoard();
  }, [loadBoard]);

  // ---- solve polling (the existing 500 ms pattern) -------------------------

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!inFlight || (inFlight.status !== "queued" && inFlight.status !== "running")) return;
    const solveId = inFlight.id;
    const timer = setInterval(() => {
      void (async () => {
        try {
          const data = await apiFetch<{ solve: BoardSolve }>(`/api/tenant/optimize/solves/${solveId}`);
          const s = data.solve;
          if (s.status === "queued" || s.status === "running") {
            setElapsedS(Math.max(0, Math.round((Date.now() - Date.parse(s.queued_at)) / 1000)));
            // The worker's honest stage stamp rides the poll (Phase B dock).
            setInFlight((prev) => (prev && prev.id === s.id && prev.stage !== s.stage ? { ...prev, status: s.status, stage: s.stage } : prev));
            return;
          }
          clearInterval(timer);
          setInFlight(null);
          setElapsedS(0);
          if (s.status === "succeeded" && s.solver_status !== "infeasible") {
            if (s.kind === "what_if" && s.proposal_id) {
              const view = await apiFetch<BoardPayload>(
                `/api/tenant/optimize/models/${modelId}/board?proposal=${s.proposal_id}`
              );
              setScenarioView(view.view);
            } else if (s.kind === "refine") {
              // The replan BECAME the Refined row server-side; reload and
              // hand the landed plan to the view's attribution strip.
              const fresh = await apiFetch<BoardPayload>(`/api/tenant/optimize/models/${modelId}/board`);
              setBoard(fresh);
              setLandedRefine(fresh.refine.refined);
              setSolveNote(null);
            } else {
              const fresh = await apiFetch<BoardPayload>(`/api/tenant/optimize/models/${modelId}/board`);
              setBoard(fresh);
              setStrip(fresh.standing?.versus_previous ?? null);
              setSolveNote(null);
            }
          } else if (s.status === "succeeded" && s.solver_status === "infeasible") {
            setSolveNote(s.error ?? "No feasible schedule under the given pins, disruptions and calendar.");
          } else if (s.status === "failed") {
            toast.push({ kind: "error", message: s.error ?? "The solve failed." });
          } else if (s.status === "cancelled") {
            toast.push({ kind: "info", message: "Solve cancelled." });
          }
        } catch {
          /* transient poll error — keep polling */
        }
      })();
    }, 500);
    pollRef.current = timer;
    return () => clearInterval(timer);
  }, [inFlight, modelId, loadBoard, toast]);

  // ---- gestures ------------------------------------------------------------

  async function startSolve(kind: "solve" | "what_if") {
    if (!modelId) return;
    setBusy(true);
    setSolveNote(null);
    try {
      const body: Record<string, unknown> = { kind };
      if (kind === "what_if") body.overlays_inline = scenarioEntries;
      const data = await apiFetch<{ solve: BoardSolve }>(`/api/tenant/optimize/models/${modelId}/solve`, {
        method: "POST",
        body,
      });
      setInFlight(data.solve);
      setElapsedS(0);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
      // A solve already in flight: adopt it and watch honestly.
      try {
        const fresh = await apiFetch<BoardPayload>(`/api/tenant/optimize/models/${modelId}/board`);
        if (fresh.in_flight) setInFlight(fresh.in_flight);
      } catch {
        /* board refetch is best-effort here */
      }
    } finally {
      setBusy(false);
    }
  }

  async function cancelSolve() {
    if (!inFlight) return;
    try {
      await apiFetch(`/api/tenant/optimize/solves/${inFlight.id}/cancel`, { method: "POST" });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  async function assertOverlay(entry: ScenarioEntry & { note: string | null }) {
    if (!modelId) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/optimize/models/${modelId}/overlays`, {
        method: "POST",
        body: entry,
      });
      await loadBoard({ quiet: true });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function clearOverlay(overlayId: string) {
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/optimize/overlays/${overlayId}/clear`, { method: "POST" });
      await loadBoard({ quiet: true });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function promoteScenario() {
    if (!scenarioView) return;
    const ok = await confirm.confirm({
      title: "Make this the plan?",
      detail:
        "The scenario becomes the standing plan, exactly as reviewed — no re-solve. The change is recorded under your name.",
      confirmLabel: "Make this the plan",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/optimize/proposals/${scenarioView.id}/promote`, { method: "POST" });
      toast.push({ kind: "success", message: "This is now the standing plan." });
      discardScenario();
      const fresh = await apiFetch<BoardPayload>(`/api/tenant/optimize/models/${modelId}/board`);
      setBoard(fresh);
      setStrip(fresh.standing?.versus_previous ?? null);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  function discardScenario() {
    setScenario(false);
    setScenarioEntries([]);
    setScenarioView(null);
  }

  // ---- Phase B: the Refine loop's writes ------------------------------------

  /** Replace the pending moves whole; a NAMED refusal comes back as text for the popover. */
  async function setRefineMoves(moves: Placement[]): Promise<string | null> {
    if (!modelId) return null;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/optimize/models/${modelId}/refine/moves`, { method: "PUT", body: { moves } });
      await loadBoard({ quiet: true });
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : String(e);
    } finally {
      setBusy(false);
    }
  }

  async function replan() {
    if (!modelId) return;
    setBusy(true);
    setSolveNote(null);
    setLandedRefine(null);
    try {
      const data = await apiFetch<{ solve: BoardSolve }>(`/api/tenant/optimize/models/${modelId}/solve`, {
        method: "POST",
        body: { kind: "refine" },
      });
      setInFlight(data.solve);
      setElapsedS(0);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      try {
        const fresh = await apiFetch<BoardPayload>(`/api/tenant/optimize/models/${modelId}/board`);
        if (fresh.in_flight) setInFlight(fresh.in_flight);
      } catch {
        /* best-effort */
      }
    } finally {
      setBusy(false);
    }
  }

  async function resetRefine(to: "plex" | "optimized") {
    if (!modelId) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/optimize/models/${modelId}/refine/reset`, { method: "POST", body: { to } });
      setLandedRefine(null);
      await loadBoard({ quiet: true });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function promoteRefined() {
    const refined = board?.refine.refined;
    if (!refined) return;
    const ok = await confirm.confirm({
      title: "Make Refined the standing plan?",
      detail:
        "The Refined plan becomes the standing plan exactly as replanned — no re-solve. Its change sheet stays attached, with your moves as tier ①. The change is recorded under your name.",
      confirmLabel: "Make Refined the standing plan",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/optimize/proposals/${refined.id}/promote`, { method: "POST" });
      toast.push({ kind: "success", message: "Refined is now the standing plan." });
      setLandedRefine(null);
      const fresh = await apiFetch<BoardPayload>(`/api/tenant/optimize/models/${modelId}/board`);
      setBoard(fresh);
      setStrip(fresh.standing?.versus_previous ?? null);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  // ---- render --------------------------------------------------------------

  if (error) return <ErrorState detail={error} />;
  if (loading || !board) return <LoadingState />;

  const standing = board.standing;
  const view = scenario && scenarioView ? scenarioView : standing;
  const canEdit = board.can_edit;
  const solving = inFlight != null && (inFlight.status === "queued" || inFlight.status === "running");
  const replanning = solving && inFlight?.kind === "refine";

  // Phase B — outside a scenario the situation line reports the REFINED plan
  // (the working copy): its own setup / savings / risk once a replan stands,
  // the standing plan's until then. The standing plan's identity stays on
  // the line as "Optimized".
  const refine = board.refine;
  const refineContext = !scenario && canEdit && !!standing;
  const refinedPlan = refineContext && refine.basis === "refined" ? refine.refined : null;
  const diff = (refinedPlan ?? view)?.diff ?? null;
  const riskOf = refinedPlan ?? view;

  const inputsChanged =
    canEdit && standing?.solve
      ? overlaysDiffer(
          board.overlays.filter((o) => !o.cleared_at).map((o) => ({ kind: o.kind, subject: o.subject, starts_at: o.starts_at, ends_at: o.ends_at })),
          standing.solve.overlay_snapshot
        )
      : false;

  // The view this model's template claims. The shell renders it blind; a
  // template without one gets the honest empty state below.
  const View = boardViewFor(board.template.id);

  // The solve gestures — rendered only with content-edit authority; the
  // Model button beside them has its own (entitlement) gate.
  function renderSolveActions() {
    if (solving) {
      return (
        <>
          <span className="pill pillWarn">
            {inFlight?.kind === "what_if" ? "Scenario solving" : inFlight?.kind === "refine" ? "Replanning" : "Solving"} — {elapsedS}s
          </span>
          <button className="btn btnSm" onClick={() => void cancelSolve()}>Cancel</button>
        </>
      );
    }
    if (scenario) {
      return (
        <>
          <button className="btn btnPrimary btnSm" onClick={() => void startSolve("what_if")} disabled={busy}>
            Solve scenario
          </button>
          {scenarioView ? (
            <button className="btn btnPrimary btnSm" onClick={() => void promoteScenario()} disabled={busy}>
              Make this the plan
            </button>
          ) : null}
          <button className="btn btnSm" onClick={discardScenario} disabled={busy}>Discard</button>
        </>
      );
    }
    return (
      <>
        {inputsChanged ? <span className="boardNudge">Inputs changed — re-solve.</span> : null}
        <button className="btn btnPrimary btnSm" onClick={() => void startSolve("solve")} disabled={busy}>
          Re-solve
        </button>
        <button className="btn btnSm" onClick={() => setScenario(true)} disabled={busy}>
          What-if
        </button>
      </>
    );
  }

  return (
    <div className={scenario ? "boardFrame boardFrameScenario" : "boardFrame"}>
      {scenario ? (
        <div className="boardScenarioBanner" role="status">
          <strong>Scenario — not the plan.</strong>
          <span className="muted"> Nothing here changes the standing plan unless you make it the plan.</span>
        </div>
      ) : null}

      {/* ── Situation line (§3.2, always visible) ─────────────────────── */}
      <div className="card boardSituation">
        <div className="boardSituationFacts">
          {standing ? (
            <>
              <span>
                {view && view.kind === "what_if" ? (
                  <>
                    <strong>Scenario</strong>
                    <span className="muted"> · vs standing plan v{standing.version} · solved {formatTimestamp(view.created_at)}</span>
                  </>
                ) : refineContext ? (
                  <>
                    <strong>Refined plan</strong>
                    <span className="muted">
                      {refinedPlan
                        ? ` · replanned ${formatTimestamp(refinedPlan.solve?.finished_at ?? refinedPlan.created_at)}${refine.updated_by_name ? ` by ${refine.updated_by_name}` : ""} · ${refinedPlan.placements.length} move${refinedPlan.placements.length === 1 ? "" : "s"} held`
                        : refine.basis === "plex"
                          ? " · working copy, started from the Plex schedule"
                          : " · working copy, started from Optimized"}
                    </span>
                    <span className="muted">
                      {" "}· Optimized = standing plan v{standing.version}, solved {formatTimestamp(standing.solve?.finished_at ?? standing.created_at)}
                    </span>
                  </>
                ) : (
                  <>
                    <strong>Standing plan v{standing.version}</strong>
                    <span className="muted"> · solved {formatTimestamp(standing.solve?.finished_at ?? standing.created_at)}</span>
                  </>
                )}
              </span>
              {diff?.setup_hours_proposed != null ? (
                <span title="Planned setup hours in the rendered plan">
                  Setup <strong>{diff.setup_hours_proposed} h</strong>
                </span>
              ) : null}
              {diff?.hours_saved != null ? (
                // Phase A — the situation line names its baseline (the
                // asterisk footnote retired with the constructed walk). The
                // one shipped template's baseline IS the Plex schedule; a
                // second template would move this naming behind the view seam.
                <span>
                  Saves <strong>{diff.hours_saved} h</strong> vs the Plex schedule
                </span>
              ) : null}
              <span>
                Promises at risk <strong>{riskOf?.promise_risk?.count ?? diff?.promises_at_risk?.length ?? 0}</strong>{" "}
                <span className="muted">
                  {riskOf?.promise_risk?.basis === "promise"
                    ? "(real promises — milestones shown on hover)"
                    : "(vs routing milestones until the next solve)"}
                </span>
              </span>
              {(standing.solve?.freshness ?? []).map((f) => (
                <span key={f.label} className="muted" title={f.operation_name ? `From ${f.operation_name}` : undefined}>
                  {f.label}: {formatTimestampOr(f.last_run_at, "unknown")}
                </span>
              ))}
            </>
          ) : (
            <span className="muted">No standing plan yet — solve to put one on the board.</span>
          )}
        </div>
        {canEdit ? <div className="boardSituationActions">{renderSolveActions()}</div> : null}
      </div>

      {solveNote ? (
        <div className="card boardInfeasible" role="alert">
          <strong>No feasible plan:</strong> {solveNote}
        </div>
      ) : null}

      {strip && !scenario ? <WhatChangedStrip versus={strip} tone="update" onDismiss={() => setStrip(null)} /> : null}
      {scenario && scenarioView?.versus_previous ? (
        <WhatChangedStrip versus={scenarioView.versus_previous} tone="scenario" />
      ) : null}

      {View ? (
        <View
          payload={board}
          view={view}
          scenario={scenario}
          scenarioEntries={scenarioEntries}
          busy={busy || solving}
          initialFocus={props.initialFocus ?? null}
          onAssertOverlay={(entry) => {
            if (scenario) {
              setScenarioEntries((prev) => [
                ...prev,
                { kind: entry.kind, subject: entry.subject, starts_at: entry.starts_at, ends_at: entry.ends_at },
              ]);
            } else {
              void assertOverlay(entry);
            }
          }}
          onClearOverlay={(id) => void clearOverlay(id)}
          onRemoveScenarioEntry={(i) => setScenarioEntries((prev) => prev.filter((_, idx) => idx !== i))}
          refine={{
            setMoves: setRefineMoves,
            replan: () => void replan(),
            reset: (to) => void resetRefine(to),
            promote: () => void promoteRefined(),
            inFlight: replanning ? { stage: inFlight?.stage ?? null, elapsedS } : null,
            lastLanded: landedRefine,
            dismissLanded: () => setLandedRefine(null),
          }}
        />
      ) : (
        <div className="card">
          <EmptyState
            title="No board for this kind of model yet."
            detail="Atlas doesn't yet have a board that can draw this model's plans. Solves still run and proposals are still recorded — there's just nothing to draw here."
          />
        </div>
      )}
    </div>
  );
}
